import { BubbleDataPoint, Chart, ChartTypeRegistry, Point, TooltipModel } from 'chart.js';
import { format } from 'date-fns';

import { formatBytes } from '~/shared/utils/formatNumber';

const getOrCreateTooltip = () => {
  let tooltipEl = document.getElementById('chartjs-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.display = 'flex';
    tooltipEl.style.flexDirection = 'column';
    tooltipEl.style.alignItems = 'center';
    tooltipEl.style.gap = '6px';
    tooltipEl.style.width = 'fit-content';
    tooltipEl.style.minWidth = '130px';
    tooltipEl.style.height = 'fit-content';
    tooltipEl.style.backgroundColor = 'white';
    tooltipEl.style.boxShadow = '6px 4px 20px 0px rgba(0, 12, 54, 0.15)';
    tooltipEl.style.borderRadius = '4px';
    tooltipEl.style.padding = '6px 8px 6px 8px';
    tooltipEl.style.zIndex = '9999';
    tooltipEl.id = 'chartjs-tooltip';

    document.body.appendChild(tooltipEl);
  }

  return tooltipEl;
};

export const ExternalTooltipHandler = (context: {
  chart: Chart<
    keyof ChartTypeRegistry,
    (number | Point | [number, number] | BubbleDataPoint | null)[],
    unknown
  >;
  tooltip: TooltipModel<'line'>;
}) => {
  const { tooltip } = context;

  // Tooltip Element
  const tooltipEl = getOrCreateTooltip();

  // Hide if no tooltip
  const tooltipModel = tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  // Set Text

  if (tooltipModel.body && tooltipModel.dataPoints.length > 0) {
    // Added check for dataPoints length
    let innerHtml = '';
    const rawValuestyle = 'font-weight: 400px; line-height: 20px; font-size: 14px; color: black;';

    const rawYValue = tooltipModel.dataPoints[0].parsed?.y;
    const datasetLabel = tooltipModel.dataPoints[0].dataset?.label;

    const numericValue = typeof rawYValue === 'number' ? rawYValue : 0;

    // Check if this is a utilization percentage chart (label contains 'utilization')
    const isUtilization = !!datasetLabel && datasetLabel.toLowerCase().includes('utilization');

    if (isUtilization) {
      innerHtml += `<div style="${rawValuestyle}">${numericValue.toFixed(1)}%</div>`;
    } else {
      innerHtml += `<div style="${rawValuestyle}">${formatBytes(
        numericValue, // Use the corrected numeric value
        1
      )}</div>`;
    }

    const dateStyles =
      'font-weight: 700; font-size: 14px; line-height: 24px; color: #949EAC; letter-spacing: -0.4px;';

    // It's better to get the precise x-value from parsed.x if you need the exact data point's timestamp
    const preciseTimestamp = tooltipModel.dataPoints[0].parsed?.x;
    const dateObj = preciseTimestamp ? new Date(preciseTimestamp) : new Date(); // Fallback if no precise timestamp

    const timeString = format(dateObj, 'hh:mm a');
    const dateStringFormatted = format(dateObj, 'dd MMM');

    innerHtml += `<div style="display: flex; flex-direction: column; align-items: center"><div style="${dateStyles}">${timeString}</div><div style="${dateStyles}">${dateStringFormatted}</div></div>`;

    tooltipEl.innerHTML = innerHtml;
  }

  const position = context.chart.canvas.getBoundingClientRect();

  // Display and position styles
  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'absolute';

  const tooltipWidth = tooltipEl.offsetWidth;
  const chartWidth = position.width;
  const caretX = tooltipModel.caretX;

  let left = position.left + window.scrollX + caretX;
  if (left + tooltipWidth > position.left + window.scrollX + chartWidth) {
    // Position tooltip to the left if it overflows to the right
    left -= tooltipWidth;
  }

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${position.top + window.scrollY + tooltipModel.caretY}px`;
  tooltipEl.style.pointerEvents = 'none';
};
